import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';

/**
 * Pre-warms the browser cache for assets that need to be present *synchronously*
 * during print preview snapshotting (logo, watermark, ...). Without this, the
 * first print preview after a cold load can render with the asset missing
 * because Chromium snapshots the document before the asset request resolves.
 *
 * Public API: {@link preload}. The preload set is declared once at app start
 * via {@link providePrintPreload} and registered as an `APP_INITIALIZER`, so
 * the cache is warm by the time the user opens any dashboard.
 *
 * Idempotent: a URL that has already been preloaded (and cached as such) is
 * skipped on subsequent `preload()` calls within the same service instance.
 */
@Injectable({ providedIn: 'root' })
export class PrintPreloadService {
  private preloaded: Record<string, true> = {};

  public preload(urls: string[]): void {
    for (const url of urls) {
      if (this.preloaded[url] === true) continue;
      this.preloaded[url] = true;
      const img = new Image();
      img.src = url;
      // `decode()` resolves once the image is fully decoded into the cache.
      // Failures are swallowed: we don't want a missing asset to break startup,
      // and the regular runtime fetch will surface the error in the console.
      img.decode?.().catch(() => undefined);
    }
  }
}

/** Assets known to be needed during print preview, preloaded at app startup. */
const DEFAULT_PRINT_ASSETS: readonly string[] = [
  '/public/img/logo-outpost24-watermark.svg',
  '/public/img/logo-outpost24.svg',
];

/**
 * Registers an `APP_INITIALIZER` that pre-warms the print-time asset cache.
 * Add to the root `ApplicationConfig.providers` alongside `providePlaceholderCatalogue()`.
 */
export function providePrintPreload(): Provider {
  return {
    provide: APP_INITIALIZER,
    useFactory: (service: PrintPreloadService) => () => service.preload([...DEFAULT_PRINT_ASSETS]),
    deps: [PrintPreloadService],
    multi: true,
  };
}
